import type { CSSProperties } from "react";

export type VoiceGradientStatus =
  | "listening"
  | "processing"
  | "speaking"
  | "error";

export function clampVoiceLevel(value: number) {
  return Math.max(0, Math.min(1, value));
}

export function voiceGradientStyle(
  status: VoiceGradientStatus,
  color: string,
  volume: number,
): CSSProperties {
  const level = clampVoiceLevel(volume);
  const reflectionIntensity = (
    status === "listening"
      ? 0.38 + level * 0.48
      : status === "speaking"
        ? 0.9
        : 0.78
  ).toFixed(2);
  const motionSpeed =
    status === "listening" ? `${(6.6 - level * 4.2).toFixed(2)}s` : undefined;

  const mode =
    status === "speaking"
      ? {
          "--voice-glass-a": "rgba(99,102,241,0.92)",
          "--voice-glass-b": "rgba(34,211,238,0.7)",
          "--voice-glass-c": "rgba(168,85,247,0.64)",
          "--voice-glass-icon": "var(--color-readable-overlay-secondary)",
          "--voice-glass-speed": "2.2s",
        }
      : status === "processing"
        ? {
            "--voice-glass-a": "rgba(48,209,88,0.88)",
            "--voice-glass-b": "rgba(120,255,174,0.62)",
            "--voice-glass-c": "rgba(22,163,74,0.5)",
            "--voice-glass-icon": "var(--color-readable-overlay-secondary)",
            "--voice-glass-speed": "2.7s",
          }
        : status === "error"
          ? {
              "--voice-glass-a": "rgba(248,113,113,0.9)",
              "--voice-glass-b": "rgba(251,146,60,0.54)",
              "--voice-glass-c": "rgba(244,63,94,0.62)",
              "--voice-glass-icon": "var(--color-readable-overlay-secondary)",
              "--voice-glass-speed": "3.1s",
            }
          : {
              "--voice-glass-a": color,
              "--voice-glass-b": "rgba(56,189,248,0.58)",
              "--voice-glass-c": "rgba(244,114,182,0.62)",
              "--voice-glass-icon": "var(--color-readable-overlay-secondary)",
              "--voice-glass-speed": "4.8s",
            };

  return {
    "--voice-glass-volume": reflectionIntensity,
    ...mode,
    "--voice-glass-speed": motionSpeed ?? mode["--voice-glass-speed"],
    "--voice-glass-travel": `${Math.round(12 + level * 18)}%`,
  } as CSSProperties;
}
